import { differenceInDays } from "date-fns";
import { object, string } from "yup";
import type {
  ChildCoveredPerson,
  ChildEntryPage,
  ChildQuote,
} from "@/api_client";
import { getErrorMessage } from "@/utils/errorMessage";
import { AgeValidator } from "@/validators/shared/ageValidators";
import { FinnishSSN } from "@/validators/shared/ssnValidator";

const validateSSN = (ssn: string) => {
  const finnishSSN = new FinnishSSN(ssn);
  return finnishSSN.validateSSN();
};

export type ChildEntryFormType = Pick<
  ChildQuote,
  "coveredPerson" | "policyHolderSsn"
>;

export const ResolveChildEntry = (content: ChildEntryPage | undefined | null) =>
  object<ChildEntryFormType>({
    coveredPerson: buildCoveredPersonSchema(content).required(),
    policyHolderSsn: string()
      .required(
        getErrorMessage(
          content?.policyHolderSsn?.validationMessages?.value,
          "required",
        ),
      )
      .test(
        "valid-ssn",
        getErrorMessage(
          content?.policyHolderSsn?.validationMessages?.value,
          "valid-ssn",
        ) ?? "",
        validateSSN,
      )
      .test(
        "is-18+",
        getErrorMessage(
          content?.policyHolderSsn?.validationMessages?.value,
          "is-18+",
        ) ?? "",
        (ssn: string) => {
          const ageValidator = new AgeValidator(ssn);
          return ageValidator.isAtLeast18;
        },
      ),
  });

const buildCoveredPersonSchema = (content: ChildEntryPage | undefined | null) =>
  object<Pick<ChildCoveredPerson, "ssn">>({
    ssn: string()
      .required(
        getErrorMessage(
          content?.childSsn?.validationMessages?.value,
          "required",
        ),
      )
      .test(
        "valid-ssn",
        getErrorMessage(
          content?.childSsn?.validationMessages?.value,
          "valid-ssn",
        ),
        validateSSN,
      )
      .test(
        "is-too-old",
        getErrorMessage(
          content?.childSsn?.validationMessages?.value,
          "is-too-old",
        ),
        (ssn: string) => {
          const ageValidator = new AgeValidator(ssn);
          if (ageValidator.age) {
            return ageValidator.age < 15;
          }
          return true;
        },
      )
      .test(
        "is-too-young",
        getErrorMessage(
          content?.childSsn?.validationMessages?.value,
          "is-too-young",
        ),
        (ssn) => {
          const ageValidator = new AgeValidator(ssn);
          if (ageValidator.birthDate) {
            const days = differenceInDays(new Date(), ageValidator.birthDate);

            return days >= 7;
          }
          return true;
        },
      ),
  });
